import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  BarChart3,
  Bell,
  BookOpenCheck,
  Check,
  ClipboardCheck,
  Crown,
  Filter,
  GraduationCap,
  History,
  Loader2,
  LockKeyhole,
  Minus,
  RotateCcw,
  Save,
  Search,
  ShieldCheck,
  UserCog,
  Users,
  X,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import DoxaSelect from "@/components/doxa/DoxaSelect";
import { EmptyState, PageHeader, SectionCard, StatCard } from "@/components/doxa/AdminSurface";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const EDITABLE_ROLES = ["ADMIN", "PROFESSOR"];
const HIDDEN_PERMISSION_KEYS = new Set(["eval.edit_published"]);

const GROUP_ICONS = {
  avaliacoes: ClipboardCheck,
  aulas: BookOpenCheck,
  gestao: Users,
  sistema: ShieldCheck,
};

const PERMISSION_META = {
  "eval.create": {
    description: "Criar novas avaliações em rascunho.",
    areaGate: "Avaliações",
  },
  "eval.edit": {
    description: "Editar estrutura, questões e público-alvo enquanto a avaliação estiver editável.",
    areaGate: "Avaliações",
  },
  "eval.lifecycle": {
    description: "Publicar, encerrar e arquivar avaliações.",
    areaGate: "Avaliações",
    sensitive: true,
  },
  "eval.view_results": {
    description: "Consultar resultados gerais e análises das avaliações.",
    areaGate: "Avaliações",
  },
  "eval.grant_attempt": {
    description: "Liberar uma nova tentativa para um aluno quando necessário.",
    areaGate: "Avaliações",
    sensitive: true,
  },
  "eval.grade": {
    description: "Acessar Correções e avaliar respostas dissertativas.",
    areaGate: "Correções",
  },
  "lesson.manage": {
    description: "Criar, editar, arquivar, reativar ou cancelar aulas.",
  },
  "lesson.operate": {
    description: "Abrir e encerrar aulas, visualizar QR Code e operar a presença.",
  },
  "attendance.correct": {
    description: "Lançar ou corrigir presença manualmente.",
    sensitive: true,
  },
  "attendance.justify": {
    description: "Registrar e alterar justificativas de ausência.",
  },
  "attendance.online_release": {
    description: "Liberar ou revogar check-in online nas aulas elegíveis.",
  },
  "content.tasks": {
    description: "Criar e administrar tarefas. A consulta da área pode continuar disponível sem esta permissão.",
  },
  "content.materials": {
    description: "Criar e administrar materiais. A consulta da área pode continuar disponível sem esta permissão.",
  },
  "content.notices": {
    description: "Criar e administrar avisos. A consulta da área pode continuar disponível sem esta permissão.",
  },
  "content.push": {
    description: "Acessar Notificações e enviar push aos públicos permitidos.",
    areaGate: "Notificações",
    sensitive: true,
  },
  "manage.classes": {
    description: "Acessar e gerenciar Turmas e Módulos.",
    areaGate: "Turmas",
  },
  "manage.cells": {
    description: "Acessar e gerenciar Células.",
    areaGate: "Células",
  },
  "manage.students": {
    description: "Gerir alunos e inscrições. A consulta da lista de alunos permanece disponível à equipe quando aplicável.",
    areaGate: "Inscrições",
    sensitive: true,
  },
  "manage.users": {
    description: "Acessar a Central de Contas e administrar usuários da equipe.",
    areaGate: "Usuários",
    sensitive: true,
  },
  "users.remove_inactive": {
    description: "Remover logicamente uma conta inativa, preservando IDs e histórico. Não concede acesso à tela de Usuários sozinho.",
    sensitive: true,
  },
  "view.reports": {
    description: "Acessar o Relatórios 2.0 e suas análises permitidas.",
    areaGate: "Relatórios",
  },
  "view.audit": {
    description: "Acessar a Auditoria operacional e consultar eventos sensíveis do sistema.",
    areaGate: "Auditoria",
    sensitive: true,
  },
};

const FILTER_OPTIONS = [
  { value: "ALL", label: "Todas" },
  { value: "ENABLED", label: "Habilitadas" },
  { value: "DISABLED", label: "Desabilitadas" },
  { value: "AREA", label: "Controlam acesso a área" },
  { value: "SENSITIVE", label: "Ações sensíveis" },
];

function roleLabel(role) {
  return role === "ADMIN" ? "Admin" : role === "PROFESSOR" ? "Professor" : role;
}

function YesCell() {
  return (
    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#E6F4EA] text-[#2F855A]" title="Permitido">
      <Check className="h-4 w-4" />
    </span>
  );
}

function NoCell() {
  return (
    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#F3EFE7] text-[#B0A79B]" title="Não permitido">
      <Minus className="h-4 w-4" />
    </span>
  );
}

function StudentDisplay({ display }) {
  if (display === "Y") return <YesCell />;
  if (display === "CONFIG") {
    return <span className="doxa-badge doxa-badge-neutral">Conforme configuração</span>;
  }
  return <NoCell />;
}

function PermissionBadge({ children, tone = "neutral" }) {
  const tones = {
    neutral: "bg-[#F3EFE7] text-[#6B655E]",
    orange: "bg-[#FFF0E9] text-[#D94A12]",
    warning: "bg-[#FFF7DF] text-[#9A6700]",
  };
  return <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${tones[tone] || tones.neutral}`}>{children}</span>;
}

export default function Permissions() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRole, setActiveRole] = useState("ADMIN");
  const [draft, setDraft] = useState({});
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [applying, setApplying] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showRestore, setShowRestore] = useState(false);
  const [pendingRole, setPendingRole] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: response } = await api.get("/permissions");
      setData(response);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visibleGroups = useMemo(() => (
    (data?.groups || [])
      .map((section) => ({
        ...section,
        permissions: section.permissions.filter((permission) => !HIDDEN_PERMISSION_KEYS.has(permission.key)),
      }))
      .filter((section) => section.permissions.length > 0)
  ), [data]);

  const staffGroups = useMemo(() => (
    visibleGroups
      .map((section) => ({
        ...section,
        permissions: section.permissions.filter((permission) => permission.kind === "STAFF"),
      }))
      .filter((section) => section.permissions.length > 0)
  ), [visibleGroups]);

  const studentPermissions = useMemo(() => (
    visibleGroups.flatMap((section) => section.permissions.filter((permission) => permission.kind === "STUDENT"))
  ), [visibleGroups]);

  const staffPermissions = useMemo(() => staffGroups.flatMap((section) => section.permissions), [staffGroups]);

  const baseline = useMemo(() => {
    const values = {};
    staffPermissions.forEach((permission) => {
      values[permission.key] = !!permission.cells?.[activeRole]?.value;
    });
    return values;
  }, [staffPermissions, activeRole]);

  const currentValues = useMemo(() => ({ ...baseline, ...draft }), [baseline, draft]);

  const changes = useMemo(() => (
    staffPermissions
      .filter((permission) => Object.prototype.hasOwnProperty.call(draft, permission.key))
      .map((permission) => ({
        key: permission.key,
        label: permission.label,
        before: baseline[permission.key],
        after: draft[permission.key],
      }))
      .filter((change) => change.before !== change.after)
  ), [staffPermissions, draft, baseline]);

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!changes.length) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [changes.length]);

  const roleCounts = useMemo(() => {
    const result = {};
    EDITABLE_ROLES.forEach((role) => {
      result[role] = staffPermissions.reduce((count, permission) => count + (permission.cells?.[role]?.value ? 1 : 0), 0);
    });
    return result;
  }, [staffPermissions]);

  const enabledCount = staffPermissions.reduce((count, permission) => count + (currentValues[permission.key] ? 1 : 0), 0);
  const disabledCount = Math.max(0, staffPermissions.length - enabledCount);

  const filteredGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    return staffGroups
      .map((section) => ({
        ...section,
        permissions: section.permissions.filter((permission) => {
          const meta = PERMISSION_META[permission.key] || {};
          const enabled = !!currentValues[permission.key];
          const matchesSearch = !query || [permission.label, permission.key, meta.description, section.label]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(query));
          if (!matchesSearch) return false;
          if (filter === "ENABLED") return enabled;
          if (filter === "DISABLED") return !enabled;
          if (filter === "AREA") return !!meta.areaGate;
          if (filter === "SENSITIVE") return !!meta.sensitive;
          return true;
        }),
      }))
      .filter((section) => section.permissions.length > 0);
  }, [staffGroups, search, filter, currentValues]);

  const setDraftValue = (key, value) => {
    setDraft((current) => {
      const next = { ...current };
      if (baseline[key] === value) delete next[key];
      else next[key] = value;
      return next;
    });
  };

  const requestRole = (role) => {
    if (role === activeRole) return;
    if (changes.length) {
      setPendingRole(role);
      return;
    }
    setDraft({});
    setSearch("");
    setFilter("ALL");
    setActiveRole(role);
  };

  const confirmRoleChange = () => {
    if (!pendingRole) return;
    const role = pendingRole;
    setDraft({});
    setSearch("");
    setFilter("ALL");
    setPendingRole(null);
    setActiveRole(role);
  };

  const applyChanges = async () => {
    if (!changes.length || applying) return;
    setApplying(true);
    try {
      await api.put(`/permissions/roles/${activeRole}`, {
        changes: changes.map((change) => ({ key: change.key, allowed: change.after })),
      });
      toast.success(`${changes.length} permissão(ões) atualizada(s) para ${roleLabel(activeRole)}.`);
      setShowReview(false);
      setDraft({});
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setApplying(false);
    }
  };

  const restoreDefaults = async () => {
    if (restoring || changes.length) return;
    setRestoring(true);
    try {
      const { data: response } = await api.post("/permissions/restore-defaults");
      toast.success(`Padrões restaurados (${response.restored} alteração(ões)).`);
      setShowRestore(false);
      setDraft({});
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setRestoring(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="mx-auto max-w-6xl p-4 sm:p-6 md:p-8" data-testid="permissions-page">
        <div className="doxa-card flex min-h-[45vh] items-center justify-center gap-2 text-sm text-[#6B655E]">
          <Loader2 className="h-5 w-5 animate-spin text-[#F75A1B]" /> Carregando permissões…
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 sm:p-6 md:p-8" data-testid="permissions-page">
      <PageHeader
        eyebrow="Sistema"
        title="Perfis e permissões"
        description="Defina com segurança o que Admin e Professor podem fazer. Master permanece com acesso total e Aluno segue regras contextuais do produto."
        meta="As autorizações reais são sempre aplicadas no backend."
        action={(
          <button
            type="button"
            onClick={() => navigate("/audit?category=permissions")}
            className="inline-flex h-10 items-center gap-2 rounded-xl border border-[#E0D9CD] bg-white px-3.5 text-sm font-semibold text-[#4A4640] transition hover:border-[#CFC5B7] hover:bg-[#FAF7F1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/30"
            data-testid="permissions-history-btn"
          >
            <History className="h-4 w-4" /> Histórico
          </button>
        )}
      />

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-label="Resumo dos perfis">
        <StatCard title="Master" value="Total" hint="Superusuário fixo · não editável" icon={Crown} tone="orange" compact />
        <StatCard
          title="Admin"
          value={`${roleCounts.ADMIN || 0}/${staffPermissions.length}`}
          hint="permissões habilitadas"
          icon={UserCog}
          tone={activeRole === "ADMIN" ? "orange" : "neutral"}
          compact
          onClick={() => requestRole("ADMIN")}
          showActionLabel={false}
          testId="permissions-role-admin"
        />
        <StatCard
          title="Professor"
          value={`${roleCounts.PROFESSOR || 0}/${staffPermissions.length}`}
          hint="permissões habilitadas"
          icon={GraduationCap}
          tone={activeRole === "PROFESSOR" ? "orange" : "neutral"}
          compact
          onClick={() => requestRole("PROFESSOR")}
          showActionLabel={false}
          testId="permissions-role-professor"
        />
        <StatCard title="Aluno" value="Contextual" hint={`${studentPermissions.length} regra(s) informativa(s)`} icon={Users} tone="neutral" compact />
      </section>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_280px]">
        <div className="space-y-5 min-w-0">
          <SectionCard
            title={`Perfil ${roleLabel(activeRole)}`}
            description="Monte as alterações primeiro. Nada é persistido até você revisar e aplicar."
            icon={ShieldCheck}
            action={(
              <div className="inline-flex rounded-xl border border-[#E0D9CD] bg-[#FAF7F1] p-1" role="tablist" aria-label="Perfil configurável">
                {EDITABLE_ROLES.map((role) => (
                  <button
                    key={role}
                    type="button"
                    role="tab"
                    aria-selected={activeRole === role}
                    onClick={() => requestRole(role)}
                    className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${activeRole === role ? "bg-white text-[#D94A12] shadow-sm" : "text-[#6B655E] hover:text-[#1F1F1F]"}`}
                    data-testid={`permissions-tab-${role.toLowerCase()}`}
                  >
                    {roleLabel(role)}
                  </button>
                ))}
              </div>
            )}
            testId="permissions-editor"
          >
            <div className="grid gap-3 sm:grid-cols-2 mb-5">
              <div className="rounded-xl bg-[#F8F3EA] px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-[#8A8378]">Habilitadas</p>
                <p className="mt-1 font-display text-2xl font-bold text-[#1F1F1F]">{enabledCount}</p>
              </div>
              <div className="rounded-xl bg-[#F8F3EA] px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-[#8A8378]">Desabilitadas</p>
                <p className="mt-1 font-display text-2xl font-bold text-[#1F1F1F]">{disabledCount}</p>
              </div>
            </div>

            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <label className="relative min-w-0 flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9A9287]" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Buscar ação, área ou chave técnica…"
                  className="h-10 w-full rounded-xl border border-[#E0D9CD] bg-white pl-9 pr-3 text-sm outline-none transition focus:border-[#F75A1B] focus:ring-2 focus:ring-[#F75A1B]/15"
                  data-testid="permissions-search"
                />
              </label>
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-[#8A8378]" />
                <DoxaSelect
                  value={filter}
                  onValueChange={setFilter}
                  options={FILTER_OPTIONS}
                  size="compact"
                  className="min-w-[190px]"
                  ariaLabel="Filtrar permissões"
                  testId="permissions-filter"
                />
              </div>
            </div>
          </SectionCard>

          {filteredGroups.length === 0 ? (
            <SectionCard compact>
              <EmptyState
                icon={Search}
                title="Nenhuma permissão encontrada"
                description="Ajuste a busca ou o filtro para visualizar outras permissões."
                compact
              />
            </SectionCard>
          ) : (
            filteredGroups.map((section) => {
              const GroupIcon = GROUP_ICONS[section.slug] || ShieldCheck;
              return (
                <SectionCard
                  key={section.slug}
                  title={section.label}
                  description={`${section.permissions.length} ação(ões) visível(is) neste filtro.`}
                  icon={GroupIcon}
                  compact
                  testId={`perm-section-${section.slug}`}
                >
                  <div className="divide-y divide-[#EEE7DD] rounded-xl border border-[#E8E0D5] bg-white overflow-hidden">
                    {section.permissions.map((permission) => {
                      const meta = PERMISSION_META[permission.key] || {};
                      const enabled = !!currentValues[permission.key];
                      const changed = Object.prototype.hasOwnProperty.call(draft, permission.key) && baseline[permission.key] !== draft[permission.key];
                      return (
                        <div key={permission.key} className={`p-4 transition ${changed ? "bg-[#FFF9F5]" : "bg-white"}`} data-testid={`perm-row-${permission.key}`}>
                          <div className="flex items-start gap-4">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="text-sm font-semibold text-[#1F1F1F]">{permission.label}</h3>
                                {meta.areaGate && <PermissionBadge tone="orange">Acesso: {meta.areaGate}</PermissionBadge>}
                                {meta.sensitive && <PermissionBadge tone="warning">Ação sensível</PermissionBadge>}
                                {changed && <PermissionBadge tone="orange">Alterada</PermissionBadge>}
                              </div>
                              <p className="mt-1.5 text-xs leading-relaxed text-[#6B655E]">{meta.description || "Permissão operacional do Doxa School."}</p>
                              <code className="mt-2 inline-block text-[10px] text-[#9A9287]">{permission.key}</code>
                            </div>
                            <div className="shrink-0 text-right">
                              <span className={`mb-2 block text-[11px] font-semibold ${enabled ? "text-[#2F855A]" : "text-[#8A8378]"}`}>{enabled ? "Permitido" : "Não permitido"}</span>
                              <Switch
                                checked={enabled}
                                onCheckedChange={(value) => setDraftValue(permission.key, value)}
                                disabled={applying || restoring}
                                aria-label={`${roleLabel(activeRole)} - ${permission.label}`}
                                data-testid={`perm-switch-${activeRole}-${permission.key}`}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </SectionCard>
              );
            })
          )}

          <SectionCard
            title="Aluno — regras contextuais"
            description="Estas regras descrevem capacidades do Portal do Aluno e não são editáveis por perfil. Algumas dependem da configuração da avaliação ou da aula."
            icon={GraduationCap}
            compact
            testId="permissions-student-context"
          >
            <div className="grid gap-2 sm:grid-cols-2">
              {studentPermissions.map((permission) => (
                <div key={permission.key} className="flex items-center justify-between gap-3 rounded-xl border border-[#E8E0D5] bg-[#FAF7F1] px-3 py-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-[#3F3A34]">{permission.label}</p>
                    <code className="mt-1 block text-[9px] text-[#9A9287]">{permission.key}</code>
                  </div>
                  <StudentDisplay display={permission.cells?.STUDENT?.display} />
                </div>
              ))}
            </div>
          </SectionCard>
        </div>

        <aside className="space-y-4 xl:sticky xl:top-5 xl:self-start">
          <SectionCard title="Como ler esta tela" icon={LockKeyhole} compact>
            <div className="space-y-3 text-xs leading-relaxed text-[#6B655E]">
              <p><strong className="text-[#1F1F1F]">Master</strong> é superusuário permanente e não pode ter permissões removidas aqui.</p>
              <p><strong className="text-[#1F1F1F]">Admin e Professor</strong> recebem as permissões configuradas nesta tela.</p>
              <p><strong className="text-[#1F1F1F]">Aluno</strong> segue regras contextuais do produto e não usa estes toggles.</p>
              <p>Algumas chaves controlam entrada em uma área; outras controlam somente ações dentro de telas que continuam consultáveis.</p>
            </div>
          </SectionCard>

          <SectionCard title="Governança" icon={AlertTriangle} compact>
            <div className="space-y-3 text-xs leading-relaxed text-[#6B655E]">
              <p>As alterações passam a valer no backend assim que forem aplicadas.</p>
              <p>Usuários já com o sistema aberto podem precisar recarregar a página para refletir menus e botões atualizados.</p>
              <p>O histórico de mudanças permanece centralizado em Auditoria.</p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/audit?category=permissions")}
              className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-[#D94A12] hover:text-[#B73D0D]"
            >
              <History className="h-3.5 w-3.5" /> Ver histórico de permissões
            </button>
          </SectionCard>

          <button
            type="button"
            onClick={() => setShowRestore(true)}
            disabled={restoring || loading || changes.length > 0}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[#E0D9CD] bg-white px-4 py-3 text-sm font-semibold text-[#4A4640] transition hover:bg-[#FAF7F1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/30 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="restore-defaults-btn"
            title={changes.length ? "Aplique ou descarte o rascunho antes de restaurar os padrões." : undefined}
          >
            {restoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Restaurar padrões
          </button>
        </aside>
      </div>

      {changes.length > 0 && (
        <div className="sticky bottom-3 z-30 rounded-2xl border border-[#F4C7B4] bg-white p-3 shadow-[0_14px_40px_rgba(45,31,20,.14)]" data-testid="permissions-draft-bar">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#FFF0E9] text-[#F75A1B]"><Save className="h-4 w-4" /></span>
              <div>
                <p className="text-sm font-semibold text-[#1F1F1F]">{changes.length} alteração(ões) pendente(s) em {roleLabel(activeRole)}</p>
                <p className="text-xs text-[#6B655E]">Nada foi aplicado ainda.</p>
              </div>
            </div>
            <div className="flex gap-2 sm:justify-end">
              <button
                type="button"
                onClick={() => setDraft({})}
                disabled={applying}
                className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-xl border border-[#E0D9CD] px-3 text-sm font-semibold text-[#6B655E] hover:bg-[#FAF7F1] sm:flex-none"
                data-testid="permissions-discard"
              >
                <X className="h-4 w-4" /> Descartar
              </button>
              <button
                type="button"
                onClick={() => setShowReview(true)}
                disabled={applying}
                className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-[#F75A1B] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#E95016] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/30 disabled:opacity-50 sm:flex-none"
                data-testid="permissions-review"
              >
                <ShieldCheck className="h-4 w-4" /> Revisar e aplicar
              </button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={showReview} onOpenChange={(open) => { if (!applying) setShowReview(open); }}>
        <AlertDialogContent data-testid="permissions-review-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Aplicar alterações em {roleLabel(activeRole)}?</AlertDialogTitle>
            <AlertDialogDescription>
              Revise o antes e depois. Ao confirmar, o backend passará a aplicar estas permissões imediatamente para todos os usuários deste perfil.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-[42vh] space-y-2 overflow-y-auto pr-1">
            {changes.map((change) => (
              <div key={change.key} className="rounded-xl border border-[#E8E0D5] bg-[#FAF7F1] p-3">
                <p className="text-sm font-semibold text-[#1F1F1F]">{change.label}</p>
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <span className={`rounded-full px-2 py-1 font-semibold ${change.before ? "bg-[#E6F4EA] text-[#2F855A]" : "bg-[#F3EFE7] text-[#6B655E]"}`}>{change.before ? "Permitido" : "Não permitido"}</span>
                  <span className="text-[#9A9287]">→</span>
                  <span className={`rounded-full px-2 py-1 font-semibold ${change.after ? "bg-[#E6F4EA] text-[#2F855A]" : "bg-[#F3EFE7] text-[#6B655E]"}`}>{change.after ? "Permitido" : "Não permitido"}</span>
                </div>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={applying}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => { event.preventDefault(); applyChanges(); }}
              disabled={applying || !changes.length}
              data-testid="permissions-apply"
              className="inline-flex items-center gap-2"
            >
              {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Aplicar {changes.length} alteração(ões)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingRole} onOpenChange={(open) => { if (!open) setPendingRole(null); }}>
        <AlertDialogContent data-testid="permissions-role-change-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar alterações não aplicadas?</AlertDialogTitle>
            <AlertDialogDescription>
              Você possui {changes.length} alteração(ões) pendente(s) em {roleLabel(activeRole)}. Para trocar para {roleLabel(pendingRole)}, este rascunho precisa ser descartado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar editando</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRoleChange}>Descartar e trocar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showRestore} onOpenChange={(open) => { if (!restoring) setShowRestore(open); }}>
        <AlertDialogContent data-testid="restore-defaults-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurar permissões padrão?</AlertDialogTitle>
            <AlertDialogDescription>
              As permissões de <strong>Admin</strong> e <strong>Professor</strong> voltarão juntas à configuração padrão do sistema. A autorização no backend muda imediatamente. Master e Aluno não são afetados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-xl bg-[#FFF7DF] p-3 text-xs leading-relaxed text-[#7A5A00]">
            Use esta ação quando quiser desfazer personalizações dos dois perfis de uma só vez. O histórico da restauração continua registrado.
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoring} data-testid="restore-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => { event.preventDefault(); restoreDefaults(); }}
              disabled={restoring}
              data-testid="restore-confirm"
              className="inline-flex items-center gap-2"
            >
              {restoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Restaurar padrões
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
